import * as React from 'react';
import { makeStyles, mergeClasses, shorthands, Spinner, Title1, Toaster, tokens } from '@fluentui/react-components';
import {
  Home20Filled,
  Home20Regular,
  Library20Filled,
  Library20Regular,
  Person20Filled,
  Person20Regular,
  QuestionRegular,
  Ribbon20Filled,
  Ribbon20Regular,
  Sparkle24Filled,
  Sparkle24Regular,
  Wand20Filled,
  Wand20Regular,
} from '@fluentui/react-icons';
import loadable from '@loadable/component';
import { defineMessage, useIntl } from 'react-intl';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { type User, useUserQuery } from '@foundershub/api-client';
import { AssumeUserBanner, DemoUserBanner, Header, NavOptions, SkipToMain } from '../components';
import { CenteredLoadingSpinner } from '../components/CenteredLoadingSpinner';
import { CopilotContext as FHCopilotContext, DrawerContext } from '../components/FoundersHubUserOverlayDrawer';
import { TabNavigation } from '../components/TabNavigation';
import type { NavigationTab } from '../components/TabNavigationTypes';
import { breakpoints, screenSizes } from '../modules/breakpoints';
import { useAccountRequired, useFeatureFlagEnabled, useUserRequired } from '../store';

const ChatSideCar = loadable(() => import(/* webpackChunkName: "chat-sidecar" */ '../components/ChatSideCar'), {
  resolveComponent: (components) => components.ChatSideCar,
});
const CopilotFirstTime = loadable(
  () => import(/* webpackChunkName: "copilot-first-time" */ '../modals/CopilotFirstTime'),
  { resolveComponent: (components) => components.CopilotFirstTime }
);
const FirstTimeModal = loadable(() => import(/* webpackChunkName: "first-time" */ '../pages/FirstTimeModal'));
const SupportDrawer = loadable(
  () => import(/* webpackChunkName: "support-drawer" */ '../pages/SupportForm/SupportDrawer'),
  {
    fallback: <CenteredLoadingSpinner />,
  }
);

interface Props {
  children: React.ReactNode;
}

export default function AccountRequiredRedirectLayout(props: Props) {
  useUserRequired();
  const { data: user, isLoading, isFetching } = useUserQuery();

  if ((isLoading || isFetching) && user?.accounts.length === 0) {
    return <Spinner />;
  }

  if (user?.accounts.length === 0) {
    return <Redirect to="/apply" />;
  }

  return <Layout {...props} />;
}

function Layout({ children }: Props) {
  const location = useLocation();
  const classes = useStyles();
  const intl = useIntl();
  const account = useAccountRequired();
  const user = useUserRequired();
  const history = useHistory();
  const isSettingsRoute = history.location.pathname.split('/')?.[1] === 'settings';
  const [toasterHorizontalOffset] = React.useState<number>(10);
  const [toasterVerticalOffset] = React.useState<number>(39);

  const { openCopilot, closeCopilot, isSidecarOpen, isFREOpen } = React.useContext(FHCopilotContext);

  const copilotEnabled = useFeatureFlagEnabled('copilot');
  const buildWithAITabEnabled = useFeatureFlagEnabled('build-with-ai');

  const [showFirstTimeModal, setShowFirstTimeModal] = React.useState<boolean>(false);
  const { setShowDrawer, setChildren } = React.useContext(DrawerContext);

  function setShowSupportDrawer() {
    setShowDrawer(true);
    setChildren(<SupportDrawer />);
    if (history.location.pathname.startsWith('/benefits')) {
      history.replace('/benefits');
    } else if (history.location.pathname.startsWith('/ai')) {
      history.replace('/ai');
    } else if (
      history.location.pathname.startsWith('/task/redeem-benefit') ||
      history.location.pathname.startsWith('/redeem-benefit')
    ) {
      history.replace('/');
    }
  }

  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('firsttime')) {
      setShowFirstTimeModal(true);
    } else if ((user?.accounts.length || 0) > 0) {
      setShowFirstTimeModal(shouldShowFirsttimeModal(user));
    }
  }, []);

  const headerOptions = [
    ...NavOptions,
    {
      title: defineMessage({ defaultMessage: 'Help', description: 'help' }),
      icon: <QuestionRegular />,
      onClick: setShowSupportDrawer,
    },
  ];
  const noConvoHistory = !user.signed_copilot_agreement_on;

  if (copilotEnabled) {
    headerOptions.unshift({
      title: defineMessage({ defaultMessage: 'AI assistant', description: 'AI assistant' }),
      icon: isSidecarOpen ? (
        <Sparkle24Filled className={classes.copilotIcon} />
      ) : (
        <Sparkle24Regular className={classes.copilotIcon} />
      ),
      tooltip:
        noConvoHistory && !isSidecarOpen
          ? defineMessage({
              defaultMessage: 'Get started with AI assistant, the new Founders Hub AI assistant.',
              description: 'Get started with AI assistant prompt',
            })
          : undefined,
      onClick: isSidecarOpen ? closeCopilot : () => openCopilot('fh-header'),
    });
  }

  const tabs: Array<NavigationTab> = [
    {
      id: '/',
      name: intl.formatMessage(companyProfileNavLabel),
      iconRegular: <Home20Regular />,
      iconFilled: <Home20Filled />,
    },
    {
      id: '/benefits',
      name: intl.formatMessage(benefitsNavLabel),
      iconRegular: <Ribbon20Regular />,
      iconFilled: <Ribbon20Filled />,
    },
    {
      id: '/guidance',
      name: intl.formatMessage(guidanceLabel),
      iconRegular: <Person20Regular />,
      iconFilled: <Person20Filled />,
    },
    {
      id: '/discover',
      name: intl.formatMessage(discoverNavLabel),
      iconRegular: <Library20Regular />,
      iconFilled: <Library20Filled />,
    },
  ];

  const tabsWithAI: Array<NavigationTab> = [
    ...tabs,
    {
      id: '/ai',
      name: intl.formatMessage(buildWithAINavLabel),
      iconRegular: <Wand20Regular />,
      iconFilled: <Wand20Filled />,
    },
  ];

  return (
    <div className={classes.root}>
      <SkipToMain id="main" />
      {showFirstTimeModal && <FirstTimeModal closeModal={() => setShowFirstTimeModal(false)} />}
      {copilotEnabled && isFREOpen && <CopilotFirstTime />}
      {user.perms.includes('is_assumed_user') ? <AssumeUserBanner /> : null}
      {user.perms.includes('is_demo_user') ? <DemoUserBanner /> : null}
      <Header options={headerOptions} />
      <div className={classes.main}>
        <main
          id="main"
          className={
            isSettingsRoute
              ? classes.settingsMainContent
              : copilotEnabled && isSidecarOpen
                ? mergeClasses(classes.mainContent, classes.mainContentWithCopilot)
                : classes.mainContent
          }
        >
          <Toaster
            offset={{ horizontal: toasterHorizontalOffset, vertical: toasterVerticalOffset }}
            toasterId={account.id}
          />
          {!isSettingsRoute && (
            <div className={classes.navHeader}>
              <Title1 as="h1">{account.name}</Title1>
              <div className={classes.tabs}>
                <TabNavigation tabs={buildWithAITabEnabled ? tabsWithAI : tabs} />
              </div>
            </div>
          )}
          {children}
        </main>
        {copilotEnabled && isSidecarOpen ? <ChatSideCar /> : null}
      </div>
    </div>
  );
}

function shouldShowFirsttimeModal(user: User): boolean {
  return !user.has_signed_agreement;
}

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    maxWidth: '100vw',
    alignItems: 'stretch',
  },
  main: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    flexBasis: 0,
    flexGrow: 1,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: 0,
    flexGrow: 2,
    height: '100%',
    ...shorthands.margin(0, 0, '5%', 0),
    overflowX: 'hidden', // needed for when the vertical scrollbar is present on benefits page
    maxWidth: 'calc(100vw - 10%)',
    ...shorthands.padding(0, '5%'),
    ...breakpoints.xxl({ maxWidth: `calc(${screenSizes.xxl} - 10%)`, ...shorthands.margin(0, 'auto', '5%', 'auto') }),
  },
  mainContentWithCopilot: {
    maxWidth: 'calc(100vw - 10% - 320px)',
    ...breakpoints.xxl({
      maxWidth: `calc(${screenSizes.xxl} - 10% - 320px)`,
      ...shorthands.margin(0, 'auto', '5%', 'auto'),
    }),
  },
  settingsMainContent: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: 0,
    flexGrow: 2,
  },
  copilotIcon: {
    width: '20px',
    height: '20px',
  },
  navHeader: {
    ...shorthands.padding(tokens.spacingVerticalXL, 0),
    maxWidth: '100vw',
  },
  tabs: {
    paddingTop: tokens.spacingVerticalXL,
  },
});

const benefitsNavLabel = defineMessage({
  defaultMessage: 'Benefits',
  description: 'Benefits navigation label in main nav drawer',
});

const buildWithAINavLabel = defineMessage({
  defaultMessage: 'Build with AI',
  description: 'Build with AI page navigation label in main nav drawer',
});

const companyProfileNavLabel = defineMessage({
  defaultMessage: 'Home',
  description: 'Company Profile navigation label in main Nav drawer',
});

const discoverNavLabel = defineMessage({
  defaultMessage: 'Discover',
  description: 'Discover page navigation label in main nav drawer',
});

const guidanceLabel = defineMessage({
  defaultMessage: 'Guidance',
  description: 'Guidance page nagivation label in main nav drawer',
});
